import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api, getCsrfToken } from '@/lib/api';

export default function TemplatesPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite = can('document_templates', 'write');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = () => {
    setLoading(true); setError(null);
    api.get('/api/document-templates')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  return (
    <>
      <Topbar
        breadcrumb={['Администрирование', 'Шаблоны документов']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">Шаблоны документов</span>
          <span style={{ marginLeft: 12, color: 'var(--fg-muted)', fontSize: 12 }}>
            всего {rows.length}
          </span>
        </div>
        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 12 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : (
            <div className="tpl-list">
              {rows.map((t) => (
                <TemplateCard key={t.code} t={t} canWrite={canWrite} onChanged={(updated) => {
                  setRows((prev) => prev.map((r) => r.code === updated.code ? updated : r));
                }} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' КБ';
  return (b / 1024 / 1024).toFixed(1) + ' МБ';
}

function TemplateCard({ t, canWrite, onChanged }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file || !canWrite) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['doc', 'docx'].includes(ext)) {
      setError('Можно загружать только .doc и .docx');
      return;
    }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/document-templates/${t.code}/file`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() || '' },
        body: fd,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(data?.error?.message || 'Не удалось загрузить файл');
      onChanged?.(data.template);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки');
    } finally { setUploading(false); }
  };

  const onPick = () => fileInputRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    upload(f);
  };
  const onDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    upload(e.dataTransfer?.files?.[0]);
  };

  const onRemove = async () => {
    if (!confirm(`Удалить файл шаблона «${t.label}»?`)) return;
    setUploading(true); setError(null);
    try {
      const res = await api.delete(`/api/document-templates/${t.code}/file`);
      onChanged?.(res.template);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
    } finally { setUploading(false); }
  };

  return (
    <div className="tpl-card">
      <div className="tpl-card__main">
        <div className="tpl-card__head">
          <span className="tpl-card__label">{t.label}</span>
          <span className="tpl-card__code">{t.code}</span>
        </div>
        {t.description && <div className="tpl-card__desc">{t.description}</div>}

        <input ref={fileInputRef} type="file" accept=".doc,.docx" style={{ display: 'none' }} onChange={onFile} />

        {t.file ? (
          <div
            className={'tpl-card__file' + (dragOver ? ' is-dragover' : '')}
            onDragOver={canWrite ? onDragOver : undefined}
            onDragLeave={canWrite ? onDragLeave : undefined}
            onDrop={canWrite ? onDrop : undefined}
          >
            <span className="tpl-card__icon">DOCX</span>
            <span className="tpl-card__filename">{t.file.originalName}</span>
            <span className="tpl-card__filemeta">
              {fmtSize(t.file.size)}
              {t.file.uploadedAt && <> · {t.file.uploadedAt.slice(0, 10).split('-').reverse().join('.')}</>}
            </span>
          </div>
        ) : (
          <div
            className={'tpl-card__file tpl-card__file--empty' + (dragOver ? ' is-dragover' : '')}
            onClick={canWrite ? onPick : undefined}
            onDragOver={canWrite ? onDragOver : undefined}
            onDragLeave={canWrite ? onDragLeave : undefined}
            onDrop={canWrite ? onDrop : undefined}
          >
            <Icon name="download" size={14} />
            <span>{uploading ? 'Загружаем…' : 'Файл не загружен. Перетащите .docx или нажмите, чтобы выбрать'}</span>
          </div>
        )}

        {error && <div style={{ color: 'var(--red-fg)', fontSize: 12, marginTop: 6 }}>{error}</div>}
      </div>

      <div className="tpl-card__actions">
        {t.file && (
          <a href={t.file.downloadUrl} download={t.file.originalName}>
            <Icon name="download" size={12} /> Скачать
          </a>
        )}
        {canWrite && (
          <button type="button" onClick={onPick} disabled={uploading}>
            <Icon name="plus" size={12} /> {t.file ? 'Заменить' : 'Загрузить'}
          </button>
        )}
        {canWrite && t.file && (
          <button type="button" className="is-danger" onClick={onRemove} disabled={uploading}>
            <Icon name="x" size={12} /> Удалить
          </button>
        )}
      </div>
    </div>
  );
}
